<!-- Polaris (fase 5.5): the companies list as the kit's table (areas/contacts/CompaniesList.vue), with the woot's
     data. The headers of the fields the server sorts by (the Sort menu's) are buttons in the same state as the menu
     (`companies_sort_by`), the contacts list's rule (R2-5-PC-7 = C). No selection column: the woot has no action on
     several companies. Below md, the compact items. -->
<script setup>
import { useI18n } from 'vue-i18n';
import { ArrowDown16, ArrowUp16, ArrowsVertical16 } from '@carbon/icons-vue';
import CompaniesCard from 'dashboard/components-next/Companies/CompaniesCard/CompaniesCard.vue';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import {
  Table,
  TableBody,
  TableCaption,
  TableHead,
  TableHeader,
  TableRow,
} from 'dashboard/components-next/polaris/ui/table';

const props = defineProps({
  companies: { type: Array, required: true },
  caption: { type: String, default: '' },
  openCompanyId: { type: [Number, String], default: null },
  activeSort: { type: String, default: '' },
  activeOrdering: { type: String, default: '' },
});

const emit = defineEmits(['showCompany', 'sort']);

const { t } = useI18n();

const COLUMNS = [
  {
    key: 'name',
    label: 'COMPANIES_POLARIS.LIST.COLUMNS.NAME',
    cls: 'ps-4',
  },
  {
    key: 'domain',
    label: 'COMPANIES_POLARIS.LIST.COLUMNS.DOMAIN',
    cls: 'hidden @[44rem]/list:table-cell',
  },
  {
    key: 'contacts_count',
    label: 'COMPANIES_POLARIS.LIST.COLUMNS.CONTACTS',
    cls: '',
  },
  {
    key: 'last_activity_at',
    label: 'COMPANIES_POLARIS.LIST.COLUMNS.LAST_ACTIVITY',
    cls: 'hidden pe-4 @[31rem]/list:table-cell',
  },
];

const sortOf = key =>
  props.activeSort === key
    ? { '': 'ascending', '-': 'descending' }[props.activeOrdering]
    : 'none';
const sortBy = key =>
  emit('sort', {
    sort: key,
    order: sortOf(key) === 'ascending' ? '-' : '',
  });
const isOpen = id =>
  props.openCompanyId !== null && String(props.openCompanyId) === String(id);
</script>

<template>
  <div class="[&_[data-slot=table-container]]:overflow-visible">
    <Table class="hidden md:table" data-companies-table>
      <TableCaption class="sr-only">{{ caption }}</TableCaption>
      <TableHeader class="sticky top-0 z-10 bg-polaris-background">
        <TableRow class="hover:bg-transparent">
          <TableHead
            v-for="column in COLUMNS"
            :key="column.key"
            :class="column.cls"
            :aria-sort="sortOf(column.key)"
          >
            <Button
              variant="ghost"
              size="sm"
              class="-mx-2.5"
              :data-sort-column="column.key"
              @click="sortBy(column.key)"
            >
              <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
              {{ t(column.label) }}
              <ArrowUp16
                v-if="sortOf(column.key) === 'ascending'"
                aria-hidden="true"
              />
              <ArrowDown16
                v-else-if="sortOf(column.key) === 'descending'"
                aria-hidden="true"
              />
              <ArrowsVertical16
                v-else
                class="text-polaris-muted-foreground"
                aria-hidden="true"
              />
            </Button>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <CompaniesCard
          v-for="company in companies"
          :id="company.id"
          :key="company.id"
          :name="company.name"
          :domain="company.domain"
          :contacts-count="company.contactsCount || 0"
          :avatar-url="company.avatarUrl"
          :last-activity-at="company.lastActivityAt"
          :is-open="isOpen(company.id)"
          @show-company="id => emit('showCompany', id)"
        />
      </TableBody>
    </Table>
    <ul
      class="m-0 list-none border-0 border-t border-solid border-polaris-border p-0 md:hidden"
      :aria-label="caption"
      data-companies-items
    >
      <CompaniesCard
        v-for="company in companies"
        :id="company.id"
        :key="company.id"
        layout="item"
        :name="company.name"
        :domain="company.domain"
        :contacts-count="company.contactsCount || 0"
        :avatar-url="company.avatarUrl"
        :last-activity-at="company.lastActivityAt"
        :is-open="isOpen(company.id)"
        @show-company="id => emit('showCompany', id)"
      />
    </ul>
  </div>
</template>
